/**
 * Article markdown -> HTML, with fenced code coloured by Shiki.
 *
 * One renderer for the two places that turn an article into HTML: the SPA
 * (articles.ts, in the browser) and the build-time prerender
 * (build/seo-plugins.ts, in Node), so the static copy of an article and the
 * page the app draws are the same HTML, colours included.
 *
 * Nothing is downloaded that an article does not need. The highlighter's
 * core, engine and theme (about 180 KB) and each grammar (3 KB for JSON up to
 * 180 KB for TypeScript) are separate chunks, loaded through dynamic imports
 * only when `prepareHighlighter()` is asked for a language. An article with
 * no fenced code loads none of it; one with a JavaScript block loads the core
 * and that one grammar. `renderArticleHtml()` is synchronous: it colours the
 * fences whose grammar is loaded and leaves the rest as marked's plain
 * `<pre><code class="language-x">` (unknown languages, or a load that failed),
 * so the text is never held back by the colours.
 *
 * Callers: articles.ts awaits `prepareArticleHighlighting(markdown)` before
 * rendering; the prerender prepares the union of every article's languages
 * once per build. Theme: `github-dark`, the dark half of the docs site's
 * default pair, on the article's own dark code panel (article.vue keeps the
 * panel's background and padding; the theme's background is stripped from
 * Shiki's output so the two cannot disagree). A fence's info string is matched
 * case-insensitively through ALIASES; anything after the first word
 * (`js{2,4}`, `js title=...`) is ignored.
 */
import { Marked } from "marked";
import type { HighlighterCore } from "shiki/core";
import { ARTICLE_MARKED_OPTIONS, articleBodyMarkdown } from "./articles-meta";

export const CODE_THEME = "github-dark";

/** Grammar ids that can be loaded (Shiki ids). One dynamic import each, below. */
export const LANGS = ["javascript", "typescript", "json", "html", "css", "sql", "bash", "python", "yaml", "vue"] as const;
export type Grammar = (typeof LANGS)[number];

/** Fence words -> grammar id. Anything else renders plain. */
const ALIASES: Record<string, Grammar> = {
    javascript: "javascript", js: "javascript", mjs: "javascript", cjs: "javascript", jsx: "javascript", node: "javascript",
    typescript: "typescript", ts: "typescript", tsx: "typescript", mts: "typescript",
    json: "json", jsonc: "json", json5: "json",
    html: "html", htm: "html",
    css: "css",
    sql: "sql",
    bash: "bash", sh: "bash", shell: "bash", zsh: "bash", console: "bash",
    python: "python", py: "python",
    yaml: "yaml", yml: "yaml",
    vue: "vue",
};

/** Named explicitly, one per grammar, so the bundler can see each chunk. */
const GRAMMAR_LOADERS: Record<Grammar, () => Promise<{ default: any }>> = {
    javascript: () => import("@shikijs/langs/javascript"),
    typescript: () => import("@shikijs/langs/typescript"),
    json: () => import("@shikijs/langs/json"),
    html: () => import("@shikijs/langs/html"),
    css: () => import("@shikijs/langs/css"),
    sql: () => import("@shikijs/langs/sql"),
    bash: () => import("@shikijs/langs/bash"),
    python: () => import("@shikijs/langs/python"),
    yaml: () => import("@shikijs/langs/yaml"),
    vue: () => import("@shikijs/langs/vue"),
};

/** The grammar id for a fence's info string, or null when it is not one we load. */
export function grammarFor(info: string | undefined | null): Grammar | null {
    const word = String(info || "").trim().split(/[\s{:,]/, 1)[0].toLowerCase();
    return word ? ALIASES[word] || null : null;
}

/**
 * The grammars an article's fences name, in order of first use, each once. A
 * closing fence has no word after it and an untagged fence has none either, so
 * both fall out; the scan does not have to pair the fences.
 */
export function languagesIn(markdown: string): Grammar[] {
    const found: Grammar[] = [];
    const fence = /^[ \t]{0,3}(?:`{3,}|~{3,})[ \t]*([^\s`~]+)/gm;
    let m: RegExpExecArray | null;
    while ((m = fence.exec(markdown))) {
        const g = grammarFor(m[1]);
        if (g && !found.includes(g)) found.push(g);
    }
    return found;
}

let core: HighlighterCore | null = null;
let coreLoading: Promise<HighlighterCore> | null = null;
const loaded = new Set<Grammar>();
const grammarLoading = new Map<Grammar, Promise<void>>();

function loadCore(): Promise<HighlighterCore> {
    if (core) return Promise.resolve(core);
    if (coreLoading) return coreLoading;
    coreLoading = Promise.all([import("shiki/core"), import("shiki/engine/javascript"), import("@shikijs/themes/github-dark")])
        .then(async ([shiki, engine, theme]) => {
            core = await shiki.createHighlighterCore({
                themes: [theme.default],
                langs: [],
                // The JavaScript regex engine needs no WebAssembly, so it works the
                // same in the browser and in the build. `forgiving` skips a grammar
                // pattern the engine cannot translate instead of refusing the grammar.
                engine: engine.createJavaScriptRegexEngine({ forgiving: true }),
            });
            return core;
        })
        .catch((err) => {
            coreLoading = null;
            throw err;
        });
    return coreLoading;
}

/**
 * Make these grammars renderable: the core once, then each grammar not yet
 * loaded. Resolves at once for an empty list. Every call for the same grammar
 * shares one load; a failed load is forgotten so the next call tries again.
 */
export async function prepareHighlighter(langs: readonly Grammar[]): Promise<void> {
    const missing = langs.filter((l) => !loaded.has(l));
    if (!missing.length) return;
    const hl = await loadCore();
    await Promise.all(
        missing.map((lang) => {
            if (loaded.has(lang)) return Promise.resolve();
            let p = grammarLoading.get(lang);
            if (!p) {
                p = hl
                    .loadLanguage(GRAMMAR_LOADERS[lang]())
                    .then(() => {
                        loaded.add(lang);
                    })
                    .finally(() => {
                        grammarLoading.delete(lang);
                    });
                grammarLoading.set(lang, p);
            }
            return p;
        }),
    );
}

/** Prepare exactly what this article's fences need (nothing for an article without code). */
export function prepareArticleHighlighting(markdown: string): Promise<void> {
    return prepareHighlighter(languagesIn(markdown));
}

/** True when a fence with this info string would be coloured right now. */
export function canHighlight(info: string | undefined | null): boolean {
    const g = grammarFor(info);
    return !!g && core !== null && loaded.has(g);
}

/**
 * Shiki's HTML for one fence, or null when its grammar is not loaded (the
 * caller then falls back to marked's default). Shiki's `<pre>` carries the
 * theme's background and text colour inline; those are dropped so the
 * article's own code panel styles apply, and the `<code>` gets the
 * `language-<id>` class marked would have written, so the HTML keeps the same
 * shape with or without colours.
 */
function highlightedBlock(code: string, info: string | undefined): string | null {
    const lang = grammarFor(info);
    if (!lang || !core || !loaded.has(lang)) return null;
    try {
        return core.codeToHtml(code, {
            lang,
            theme: CODE_THEME,
            transformers: [
                {
                    pre(node) {
                        delete node.properties.style;
                    },
                    code(node) {
                        node.properties.class = `language-${lang}`;
                    },
                },
            ],
        });
    } catch {
        return null;
    }
}

const renderer = new Marked({
    ...ARTICLE_MARKED_OPTIONS,
    renderer: {
        code(token) {
            // `false` hands the block back to marked's own renderer.
            return highlightedBlock(token.text, token.lang) ?? false;
        },
    },
});

/**
 * An article's body as HTML: the title line dropped (the page header shows
 * it), `# ` headings demoted to h2, fenced code coloured where its grammar is
 * loaded. Synchronous, so the prerender can call it inside its page loop.
 */
export function renderArticleHtml(markdown: string): string {
    return renderer.parse(articleBodyMarkdown(markdown)) as string;
}
